import { ProfileCustomValuesList } from "PFApp/components/custom_values_list";
import { useProfilePreviewData } from "PFApp/use_profile_preview";
import { isRankable } from "PFCore/helpers/custom_type";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { CustomType, Profile, PureCustomValue } from "PFTypes";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getVisibleCustomTypes } from "./profile_preview_custom_fields.utils";
import { ProfilePreviewHeader } from "./profile_preview_header";

type ProfilePreviewCustomFieldsProps = {
  only?: string[];
};

export const ProfilePreviewCustomFields = ({ only }: ProfilePreviewCustomFieldsProps) => {
  const {
    data: { profile, ready }
  } = useProfilePreviewData();

  const { data: currentProfile } = useCurrentProfile();
  const { customTypes } = useCustomTypes();

  const types = useMemo(
    () => getVisibleCustomTypes(profile, currentProfile, customTypes, only),
    [currentProfile, profile, customTypes, only]
  );

  if (!ready || !profile || !types || types.length === 0) {
    return null;
  }

  // NOTE: don't wrap in it's own <div> so the <ProfilePreviewHeader> can manage spacing
  // NOTE: the order of custom fields is the same as provided by api in the /me endpoint
  return (
    <>
      {types.map((type) => (
        <Fragment key={type.name}>
          <Section
            type={type}
            values={profile[type.name] as PureCustomValue[] | undefined}
            profile={profile}
          />
        </Fragment>
      ))}
    </>
  );
};

type SectionProps = {
  values?: PureCustomValue[];
  type: CustomType;
  profile: Profile;
};

const Section = ({ values, type, profile }: SectionProps) => {
  const { t } = useTranslation("core");

  return (
    <div key={`ProfilePreviewCustomFields.${type.name}`}>
      <ProfilePreviewHeader>{type.display_as}</ProfilePreviewHeader>
      {values && values.length > 0 ? (
        <ProfileCustomValuesList
          customFieldName={type.name}
          customValues={values}
          profileId={profile.id}
          limit={10}
          showLeftIcon={isRankable(type)}
        />
      ) : (
        t("profilePreview.noInformation")
      )}
    </div>
  );
};
